import {
  defaultInitialsFieldWidthPx,
  defaultSignatureFieldWidthPx,
  GQL,
  initialsPlaceholderLayoutPx,
  signaturePlaceholderLayoutPx,
} from '@aline/common';
import { caveat, loadFont } from '@aline/fonts/client';
import { getStampTopLabel, STAMP_BOTTOM_LABEL } from 'src/helpers/signature-labels';

interface FramedSigPreviewOptions {
  bracketColor: string;
  fallbackText: string;
  hPx: number;
  previewInfo: {
    textOrDataUrl: string;
    type: 'text' | 'dataUrl';
  };
  /** Picks the bracket geometry, the default width it scales from, and the top label. */
  variant: GQL.SignatureFieldVariant;
  wPx: number;
}

const SIGNATURE_FONT_PX = 24;
/**
 * The adopted canvas is uploaded as the stamp image, so its resolution is what a zoomed-in
 * PDF shows. It is fixed rather than the screen's pixel ratio: 1x left the 8px labels
 * blurry, and a 4x stamp (1024x240 for a signature) makes saving the PDF's annotations hang.
 */
const STAMP_PIXEL_RATIO = 3;

export async function drawFramedSigPreview(
  canvas: HTMLCanvasElement,
  options: FramedSigPreviewOptions
): Promise<void> {
  const {
    bracketColor,
    fallbackText,
    hPx,
    previewInfo: { textOrDataUrl, type },
    variant,
    wPx,
  } = options;
  const isInitials = variant === GQL.SignatureFieldVariant.Initials;
  const layout = isInitials ? initialsPlaceholderLayoutPx : signaturePlaceholderLayoutPx;
  const defaultWidth = isInitials ? defaultInitialsFieldWidthPx : defaultSignatureFieldWidthPx;
  await loadFont(caveat, { family: 'Caveat' });

  // Canvas dims match the widget rect so the flattened stamp doesn't squish.
  const ctx = createHiResCtx(canvas, { hPx, wPx });
  if (!ctx) return;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const scale = wPx / defaultWidth;
  const cornerR = layout.cornerR * scale;
  const topBracketRightX = cornerR + layout.topStub * scale;
  const bottomBracketRightX = cornerR + layout.bottomStub * scale;
  /**
   * The labels are baked in so the flattened PDF keeps them. The bracket is inset by
   * half a label height so they straddle its stubs inside the canvas, the same layout
   * as `SignatureFieldPlaceholder`, so the signed stamp lines up with the placeholder.
   */
  const frameTop = layout.labelFontPx / 2;
  const frameBottom = hPx - layout.labelFontPx / 2;
  const outlineLeft = layout.strokeWidthPx / 2;

  ctx.strokeStyle = bracketColor;
  ctx.lineWidth = layout.strokeWidthPx;
  ctx.lineCap = 'butt';
  ctx.beginPath();
  ctx.moveTo(topBracketRightX, frameTop);
  ctx.lineTo(cornerR, frameTop);
  ctx.quadraticCurveTo(outlineLeft, frameTop, outlineLeft, frameTop + cornerR);
  ctx.lineTo(outlineLeft, frameBottom - cornerR);
  ctx.quadraticCurveTo(outlineLeft, frameBottom, cornerR, frameBottom);
  ctx.lineTo(bottomBracketRightX, frameBottom);
  ctx.stroke();

  const labelGap = layout.labelGap * scale;
  ctx.fillStyle = 'gray';
  // Open Sans comes in with the MUI theme, so the canvas has it without `loadFont`.
  ctx.font = `${layout.labelFontPx}px "Open Sans", sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.fillText(getStampTopLabel(isInitials), topBracketRightX + labelGap, frameTop);
  ctx.fillText(STAMP_BOTTOM_LABEL, bottomBracketRightX + labelGap, frameBottom);

  const previewStartX = 20;
  const previewRightPadding = 8;
  const previewMaxWidth = wPx - (previewStartX + previewRightPadding);
  const previewHeight = SIGNATURE_FONT_PX;

  switch (type) {
    case 'text': {
      const previewText = textOrDataUrl || fallbackText;
      ctx.font = `${SIGNATURE_FONT_PX}px Caveat`;
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'black';
      ctx.fillText(previewText, previewStartX, hPx / 2, previewMaxWidth);
      break;
    }
    case 'dataUrl': {
      await new Promise<void>(resolve => {
        const img = new Image();
        img.src = textOrDataUrl;
        img.addEventListener(
          'load',
          () => {
            ctx.drawImage(
              img,
              previewStartX,
              hPx / 2 - previewHeight / 2,
              previewMaxWidth,
              previewHeight
            );
            resolve();
          },
          false
        );
        img.addEventListener('error', () => resolve(), false);
      });
      break;
    }
  }
}

export async function getCanvasBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise(resolve => {
    canvas.toBlob(resolve);
  });
}

/**
 * Creates a 2D context backed by `STAMP_PIXEL_RATIO` device pixels per CSS pixel, so text
 * and lines stay crisp on screen and in the stamp image.
 */
function createHiResCtx(
  canvas: HTMLCanvasElement,
  options: { hPx: number; wPx: number }
): CanvasRenderingContext2D | null {
  const { hPx, wPx } = options;
  canvas.width = wPx * STAMP_PIXEL_RATIO;
  canvas.height = hPx * STAMP_PIXEL_RATIO;
  canvas.style.width = `${wPx}px`;
  canvas.style.height = `${hPx}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.scale(STAMP_PIXEL_RATIO, STAMP_PIXEL_RATIO);

  return ctx;
}
